import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { BsFiletypeXls } from "react-icons/bs";
import { CgUnavailable } from "react-icons/cg";
import {
  FaChartLine,
  FaFolderOpen,
  FaHome,
  FaFileInvoice,
} from "react-icons/fa";
import Swal from "sweetalert2";
import "react-datepicker/dist/react-datepicker.css";

const API_URL = process.env.REACT_APP_API_URL;

function PlotLedger() {
  const [projects, setProjects] = useState([]);
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [formData, setFormData] = useState({
    project_id: "",
    plot_id: "",
    start_date: "",
    end_date: "",
  });
  const [filteredPlots, setFilteredPlots] = useState([]);

  const getAuthToken = () => localStorage.getItem("token");

  useEffect(() => {
    const fetchProjects = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
        const projectsResponse = await fetch(`${API_URL}/project-list-block`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const projectsData = await projectsResponse.json();
        if (projectsData.status) {
          setProjects(projectsData.data || []);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        Swal.fire("Error", "Failed to load projects", "error");
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchPlots = async () => {
      if (!formData.project_id) {
        setFilteredPlots([]);
        setPlots([]);
        return;
      }

      const token = getAuthToken();
      if (!token) return;
      try {
        const plotsResponse = await fetch(
          `${API_URL}/all-plot-list-by-id?project_id=${formData.project_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const plotsData = await plotsResponse.json();
        if (plotsData.status) {
          const allPlots = plotsData.data || [];
          setPlots(allPlots);
          setFilteredPlots(allPlots);
        } else {
          setPlots([]);
          setFilteredPlots([]);
        }
      } catch (err) {
        console.error("Error fetching plots:", err);
        Swal.fire("Error", "Failed to load units", "error");
      }
    };

    fetchPlots();
  }, [formData.project_id]);

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      project_id: projectId,
      plot_id: "",
    }));
    setLedgerData(null);
  };

  const handlePlotChange = (e) => {
    const plotId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      plot_id: plotId,
    }));
  };

  const fetchLedgerData = async () => {
    if (!formData.project_id || !formData.plot_id) {
      Swal.fire("Warning", "Please select both project and unit", "warning");
      return;
    }

    setLoading(true);
    const token = getAuthToken();

    try {
      const params = new URLSearchParams({
        project_id: formData.project_id,
        plot_id: formData.plot_id,
      });

      if (formData.start_date) params.append("start_date", formData.start_date);
      if (formData.end_date) params.append("end_date", formData.end_date);

      const response = await fetch(`${API_URL}/plot-ledger-report?${params}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success === "1") {
        setLedgerData(data.data);
        Swal.fire("Success", "Ledger data loaded successfully", "success");
      } else {
        Swal.fire(
          "Error",
          data.message || "Failed to load ledger data",
          "error"
        );
      }
    } catch (err) {
      console.error("Error fetching ledger data:", err);
      Swal.fire("Error", "Failed to load ledger data", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getTransactionTypeBadge = (source) => {
    const badges = {
      expenses: { bg: "#feeaeb", color: "#dc2626", label: "Expense" },
      direct_income: {
        bg: "#e7f9eb",
        color: "#059669",
        label: "Distribution Commission",
      },
      commission: {
        bg: "#fef3e2",
        color: "#d97706",
        label: "Distribution Commission",
      },
    };
    const b = badges[source] || {
      bg: "#f1f5f9",
      color: "#64748b",
      label: source,
    };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: "20px",
          fontSize: "11px",
          fontWeight: "700",
          background: b.bg,
          color: b.color,
          whiteSpace: "nowrap",
        }}
      >
        {b.label}
      </span>
    );
  };

  const handleExportExcel = () => {
    if (!ledgerData) return;

    const headers = [
      "Date",
      "Transaction ID",
      "Type",
      "Description",
      "Credit",
      "Debit",
      "Balance",
      "Customer",
      "Associate",
      "Unit Details",
    ];

    const rows = ledgerData.ledger.map((entry) => [
      entry.date,
      entry.id,
      entry.source_table,
      entry.description,
      entry.cr,
      entry.dr,
      entry.balance,
      entry.customer ? entry.customer.name : "-",
      entry.associate ? entry.associate.name : "-",
      entry.plot ? entry.plot.full_address : "-",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plot_ledger_${formData.project_id}_${formData.plot_id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // 🎨 Summary Card Colors
  const summaryColors = {
    credit: { bg: "#e7f9eb", color: "#059669", icon: "📈" },
    debit: { bg: "#feeaeb", color: "#dc2626", icon: "📉" },
    balance: { bg: "#f3e8ff", color: "#7c3aed", icon: "💰" },
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
        padding: "24px",
        fontFamily: "'Poppins', 'DM Sans', sans-serif",
      }}
    >
      <div style={{ maxWidth: "1500px", margin: "0 auto" }}>
        {/* 🎯 HEADER */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "20px 20px 0 0",
            boxShadow: "0 12px 40px rgba(30, 87, 153, 0.1)",
            border: "1.5px solid #e2e8f0",
            borderBottom: "none",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "5px",
              background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
            }}
          />

          <div
            style={{
              padding: "24px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
              background: "linear-gradient(135deg, #ffffff, #f8fafc)",
              borderBottom: "1.5px solid #e2e8f0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "14px",
                  background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "22px",
                  boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                  flexShrink: 0,
                }}
              >
                <FaChartLine />
              </div>
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#1e3a5f",
                    letterSpacing: "-0.3px",
                  }}
                >
                  Unit Ledger Report
                </h2>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "12.5px",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  View transaction history by project & unit
                </p>
              </div>
            </div>

            <button
              onClick={handleExportExcel}
              disabled={!ledgerData}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: ledgerData
                  ? "linear-gradient(135deg, #059669, #047857)"
                  : "#cbd5e1",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "700",
                cursor: ledgerData ? "pointer" : "not-allowed",
                transition: "all 0.25s ease",
                boxShadow: ledgerData
                  ? "0 6px 18px rgba(5, 150, 105, 0.3)"
                  : "none",
                opacity: ledgerData ? 1 : 0.6,
              }}
            >
              <BsFiletypeXls style={{ fontSize: "16px" }} />
              Export CSV
            </button>
          </div>
        </div>

        {/* 🎯 BODY */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "0 0 20px 20px",
            boxShadow: "0 12px 40px rgba(30, 87, 153, 0.1)",
            border: "1.5px solid #e2e8f0",
            borderTop: "none",
            padding: "24px 30px",
          }}
        >
          {/* Filters */}
          <div
            style={{
              background: "#f8fafc",
              borderRadius: "14px",
              padding: "18px",
              marginBottom: "24px",
              border: "1.5px solid #e2e8f0",
            }}
          >
            <Row className="gy-3">
              <Col md={5}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12.5px",
                    fontWeight: "700",
                    color: "#1e3a5f",
                    marginBottom: "6px",
                  }}
                >
                  Project <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#1e5799",
                      fontSize: "14px",
                      pointerEvents: "none",
                    }}
                  >
                    <FaFolderOpen />
                  </span>
                  <select
                    value={formData.project_id}
                    onChange={handleProjectChange}
                    style={{
                      width: "100%",
                      padding: "11px 14px 11px 38px",
                      borderRadius: "10px",
                      border: "1.5px solid #e2e8f0",
                      background: "#ffffff",
                      fontSize: "13.5px",
                      fontWeight: "500",
                      color: "#1e3a5f",
                      cursor: "pointer",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>

              <Col md={5}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12.5px",
                    fontWeight: "700",
                    color: "#1e3a5f",
                    marginBottom: "6px",
                  }}
                >
                  Unit <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#1e5799",
                      fontSize: "14px",
                      pointerEvents: "none",
                    }}
                  >
                    <FaHome />
                  </span>
                  <select
                    value={formData.plot_id}
                    onChange={handlePlotChange}
                    disabled={!formData.project_id}
                    style={{
                      width: "100%",
                      padding: "11px 14px 11px 38px",
                      borderRadius: "10px",
                      border: "1.5px solid #e2e8f0",
                      background: formData.project_id
                        ? "#ffffff"
                        : "#f1f5f9",
                      fontSize: "13.5px",
                      fontWeight: "500",
                      color: "#1e3a5f",
                      cursor: formData.project_id
                        ? "pointer"
                        : "not-allowed",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Select Unit</option>
                    {filteredPlots.map((plot) => (
                      <option key={plot.id} value={plot.id}>
                        {plot.plot_shop_villa_no} - {plot.colony_name}
                        {plot.total_area ? ` (${plot.total_area} sq.yd)` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>

              <Col md={2} className="d-flex align-items-end">
                <button
                  onClick={fetchLedgerData}
                  disabled={
                    !formData.project_id || !formData.plot_id || loading
                  }
                  style={{
                    width: "100%",
                    padding: "11px 20px",
                    borderRadius: "10px",
                    border: "none",
                    background:
                      formData.project_id && formData.plot_id
                        ? "linear-gradient(135deg, #1e5799, #2b6cb0)"
                        : "#cbd5e1",
                    color: "#ffffff",
                    fontSize: "13.5px",
                    fontWeight: "700",
                    cursor:
                      formData.project_id && formData.plot_id
                        ? "pointer"
                        : "not-allowed",
                    transition: "all 0.25s ease",
                    boxShadow:
                      formData.project_id && formData.plot_id
                        ? "0 6px 18px rgba(30, 87, 153, 0.3)"
                        : "none",
                    opacity:
                      formData.project_id && formData.plot_id ? 1 : 0.6,
                  }}
                >
                  {loading ? "Loading..." : "View Ledger"}
                </button>
              </Col>
            </Row>
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "4px solid #e2e8f0",
                  borderTop: "4px solid #1e5799",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <p
                style={{
                  color: "#64748b",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                Loading ledger data...
              </p>
            </div>
          )}

          {/* Summary Cards */}
          {!loading && ledgerData && (
            <Row className="gy-3 mb-4">
              {[
                {
                  label: "Total Credit",
                  value: formatCurrency(
                    parseFloat(ledgerData.summary.total_credit)
                  ),
                  ...summaryColors.credit,
                },
                {
                  label: "Total Debit",
                  value: formatCurrency(
                    parseFloat(ledgerData.summary.total_debit)
                  ),
                  ...summaryColors.debit,
                },
                {
                  label: "Closing Balance",
                  value: formatCurrency(
                    parseFloat(ledgerData.summary.closing_balance)
                  ),
                  ...summaryColors.balance,
                },
              ].map((card, i) => (
                <Col md={4} key={i}>
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "14px",
                      padding: "18px",
                      border: "1.5px solid #e2e8f0",
                      position: "relative",
                      overflow: "hidden",
                      boxShadow: "0 4px 14px rgba(30, 87, 153, 0.06)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 28px rgba(30, 87, 153, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 14px rgba(30, 87, 153, 0.06)";
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "4px",
                        height: "100%",
                        background: card.color,
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: "11.5px",
                            fontWeight: "700",
                            color: "#64748b",
                            margin: 0,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {card.label}
                        </p>
                        <h3
                          style={{
                            fontSize: "22px",
                            fontWeight: "800",
                            color: card.color,
                            margin: "8px 0 0",
                            letterSpacing: "-0.5px",
                          }}
                        >
                          {card.value}
                        </h3>
                      </div>
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          background: card.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "18px",
                          flexShrink: 0,
                        }}
                      >
                        {card.icon}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {/* Table */}
          {!loading && ledgerData && (
            <div
              style={{
                overflowX: "auto",
                borderRadius: "14px",
                border: "1.5px solid #e2e8f0",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                    }}
                  >
                    {[
                      "Sr No.",
                      "Date",
                      "Project",
                      "Unit",
                      "Type",
                      "Category",
                      "Description",
                      "Payee Name",
                      "Associate",
                      "Credit (₹)",
                      "Debit (₹)",
                      "Balance (₹)",
                    ].map((th, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "14px 12px",
                          color: "#ffffff",
                          fontWeight: "700",
                          fontSize: "11.5px",
                          textAlign: "left",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {th}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.ledger.map((entry, index) => {
                    const balance = parseFloat(entry.balance);
                    return (
                      <tr
                        key={index}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td
                          style={{
                            padding: "12px",
                            fontWeight: "700",
                            color: "#1e5799",
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            color: "#64748b",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {entry.date
                            ? new Date(entry.date).toLocaleDateString("en-IN")
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontWeight: "600",
                            color: "#1e3a5f",
                          }}
                        >
                          {entry.project.name}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            color: "#334155",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {entry.plot.plot_shop_villa_no}
                        </td>
                        <td style={{ padding: "12px" }}>
                          {getTransactionTypeBadge(entry.source_table)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            color: "#64748b",
                          }}
                        >
                          {entry?.category?.name || "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            color: "#334155",
                            minWidth: "200px",
                            maxWidth: "300px",
                            fontSize: "12.5px",
                            lineHeight: "1.5",
                          }}
                        >
                          {entry.description}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            color: "#334155",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {entry.payment.transaction_name || "-"}
                        </td>
                        <td style={{ padding: "12px" }}>
                          {entry.associate ? (
                            <div>
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "#1e3a5f",
                                }}
                              >
                                {entry.associate.name}
                              </div>
                              <small style={{ color: "#94a3b8" }}>
                                {entry.associate.mobile}
                              </small>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontWeight: "700",
                            color: "#059669",
                            whiteSpace: "nowrap",
                            textAlign: "right",
                          }}
                        >
                          {parseFloat(entry.cr) > 0
                            ? formatCurrency(parseFloat(entry.cr))
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontWeight: "700",
                            color: "#dc2626",
                            whiteSpace: "nowrap",
                            textAlign: "right",
                          }}
                        >
                          {parseFloat(entry.dr) > 0
                            ? formatCurrency(parseFloat(entry.dr))
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontWeight: "700",
                            color: balance >= 0 ? "#1e5799" : "#dc2626",
                            whiteSpace: "nowrap",
                            textAlign: "right",
                          }}
                        >
                          {formatCurrency(balance)}
                        </td>
                      </tr>
                    );
                  })}
                  {ledgerData.ledger.length === 0 && (
                    <tr>
                      <td
                        colSpan="12"
                        style={{
                          padding: "50px",
                          textAlign: "center",
                          color: "#94a3b8",
                          fontWeight: "600",
                        }}
                      >
                        📭 No transactions found for the selected unit
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty State */}
          {!loading && !ledgerData && (
            <div style={{ textAlign: "center", padding: "70px 20px" }}>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "20px",
                  background: "#e8f1fb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  fontSize: "36px",
                  color: "#1e5799",
                }}
              >
                <FaFileInvoice />
              </div>
              <h4
                style={{
                  color: "#1e3a5f",
                  fontWeight: "800",
                  marginBottom: "8px",
                }}
              >
                No Data Available
              </h4>
              <p
                style={{ color: "#64748b", fontSize: "13.5px", margin: 0 }}
              >
                Please select a project and unit to view the ledger report
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PlotLedger;